/**
 * ポータル: チャット（1対1・グループ）
 * 左: ルーム一覧（未読数つき） 右: メッセージ（5秒ポーリング・添付対応）
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessagesSquare, Plus, Send, Paperclip, Users as UsersIcon, LogOut } from 'lucide-react';
import * as api from '../../services/api';
import { Modal, fieldStyle, labelStyle, fmtDateTime, fileUrl, EmptyState } from './ui';

interface Room { id: string; room_type: string; name: string | null; member_count: number; unread: number; last_message: { body: string | null; sender_name: string | null; created_at: string } | null; updated_at: string }
interface Msg { id: string; sender_user_id: string | null; sender_name: string | null; body: string | null; attachment_id: string | null; attachment_name?: string | null; created_at: string }
interface Member { user_id: string; user_name: string; last_read_at: string | null }
interface UserOpt { id: string; user_name: string; user_role: string }

export default function PortalChatPage() {
  const me = (() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } })();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [roomInfo, setRoomInfo] = useState<{ room_type: string; name: string | null } | null>(null);
  const [input, setInput] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<string | null>(null);
  activeRef.current = activeId;

  const loadRooms = useCallback(async () => {
    try { const r = await api.get<{ data: Room[] }>('/portal/chat/rooms'); setRooms(r.data ?? []); } catch { /* noop */ }
  }, []);

  const loadMessages = useCallback(async (roomId: string, markRead: boolean) => {
    try {
      const r = await api.get<{ data: { room: { room_type: string; name: string | null }; members: Member[]; messages: Msg[] } }>(`/portal/chat/rooms/${roomId}/messages`);
      if (activeRef.current !== roomId) return;
      setMessages(r.data.messages ?? []);
      setMembers(r.data.members ?? []);
      setRoomInfo(r.data.room);
      if (markRead) {
        await api.post(`/portal/chat/rooms/${roomId}/read`, {});
        setRooms(rs => rs.map(x => x.id === roomId ? { ...x, unread: 0 } : x));
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => { void loadRooms(); }, [loadRooms]);

  // ポーリング（ルーム一覧10秒・開いている会話5秒）
  useEffect(() => {
    const t1 = setInterval(() => void loadRooms(), 10_000);
    return () => clearInterval(t1);
  }, [loadRooms]);
  useEffect(() => {
    if (!activeId) return;
    void loadMessages(activeId, true);
    const t = setInterval(() => {
      const hadUnread = rooms.find(r => r.id === activeId)?.unread ?? 0;
      void loadMessages(activeId, hadUnread > 0);
    }, 5_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || !activeId) return;
    setInput('');
    try {
      await api.post(`/portal/chat/rooms/${activeId}/messages`, { body: text });
      await loadMessages(activeId, false);
      void loadRooms();
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '送信に失敗しました'); setInput(text); }
  };

  const sendFile = async (f: File) => {
    if (!activeId) return;
    try {
      const form = new FormData();
      form.append('file', f);
      const up = await api.postForm<{ data: { id: string } }>(`/portal/chat/rooms/${activeId}/attachments`, form);
      await api.post(`/portal/chat/rooms/${activeId}/messages`, { attachment_id: up.data.id });
      await loadMessages(activeId, false);
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '送信に失敗しました'); }
  };

  const activeRoom = rooms.find(r => r.id === activeId);

  return (
    <div className="page-wrap" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 150px)', boxSizing: 'border-box' }}>
      <div className="top-bar">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessagesSquare size={20} /> チャット
        </h1>
        <button className="btn-p" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setShowNew(true)}>
          <Plus size={15} /> 新しいチャット
        </button>
      </div>

      <div className="card" style={{ padding: 0, flex: 1, display: 'flex', overflow: 'hidden', minHeight: 420 }}>
        {/* ルーム一覧 */}
        <div style={{ width: 280, borderRight: '1px solid #f3f4f6', overflowY: 'auto', flexShrink: 0 }}>
          {rooms.length === 0 && <EmptyState img="/portal/empty-chat.png" text="チャットはまだありません" size={120} />}
          {rooms.map(r => (
            <button key={r.id} onClick={() => setActiveId(r.id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
                border: 'none', borderBottom: '1px solid #f8fafc', cursor: 'pointer',
                background: activeId === r.id ? '#eef6f3' : '#fff',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13.5, fontWeight: r.unread > 0 ? 800 : 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {r.room_type === 'group' ? `👥 ${r.name}` : r.name}
                </span>
                {r.unread > 0 && (
                  <span style={{ background: '#dc2626', color: '#fff', borderRadius: 999, fontSize: 10.5, fontWeight: 700, padding: '1px 7px', flexShrink: 0 }}>{r.unread}</span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.last_message ? `${r.last_message.sender_name ?? ''}: ${r.last_message.body ?? ''}` : '（メッセージなし）'}
              </div>
            </button>
          ))}
        </div>

        {/* 会話 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!activeId ? (
            <div className="empty" style={{ margin: 'auto' }}>左の一覧からチャットを選ぶか、「新しいチャット」を押してください</div>
          ) : (
            <>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{activeRoom?.room_type === 'group' ? `👥 ${activeRoom?.name}` : activeRoom?.name}</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{members.length}人</span>
                {roomInfo?.room_type === 'group' && (
                  <button className="btn" style={{ marginLeft: 'auto', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => setShowMembers(true)}>
                    <UsersIcon size={13} /> メンバー
                  </button>
                )}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f8fafc' }}>
                {messages.map(m => {
                  const mine = m.sender_user_id === me?.id;
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                      <div style={{ maxWidth: '72%' }}>
                        {!mine && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>{m.sender_name}</div>}
                        <div style={{
                          background: mine ? 'linear-gradient(135deg,#0f766e,#0f766e)' : '#fff',
                          color: mine ? '#fff' : '#111827',
                          border: mine ? 'none' : '1px solid #e5e7eb',
                          borderRadius: mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                          padding: '9px 13px', fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {m.attachment_id ? (
                            <a href={fileUrl(`/portal/chat/attachments/${m.attachment_id}`)} target="_blank" rel="noreferrer"
                              style={{ color: mine ? '#e6f4f1' : '#0f766e', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Paperclip size={13} /> {m.attachment_name ?? 'ファイル'}
                            </a>
                          ) : m.body}
                        </div>
                        <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 2, textAlign: mine ? 'right' : 'left' }}>{fmtDateTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div style={{ padding: 12, borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <label style={{ cursor: 'pointer', color: '#6b7280', padding: 8 }}>
                  <Paperclip size={17} />
                  <input type="file" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) void sendFile(f); e.target.value = ''; }} />
                </label>
                <textarea value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void send(); } }}
                  placeholder="メッセージを入力（Ctrl+Enterで送信）"
                  style={{ ...fieldStyle, resize: 'none', minHeight: 40, maxHeight: 120, flex: 1 }} rows={1} />
                <button className="btn-p" style={{ display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => void send()}>
                  <Send size={14} /> 送信
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showNew && (
        <NewChatModal onClose={() => setShowNew(false)} onCreated={async id => {
          setShowNew(false);
          await loadRooms();
          setActiveId(id);
        }} />
      )}

      {showMembers && activeId && (
        <MembersModal roomId={activeId} members={members}
          onClose={() => setShowMembers(false)}
          onChanged={() => { void loadMessages(activeId, false); void loadRooms(); }}
          onLeft={() => { setShowMembers(false); setActiveId(null); void loadRooms(); }} />
      )}
    </div>
  );
}

function NewChatModal({ onClose, onCreated }: { onClose: () => void; onCreated: (roomId: string) => void }) {
  const [users, setUsers] = useState<UserOpt[]>([]);
  const [mode, setMode] = useState<'dm' | 'group'>('dm');
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const me = (() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } })();

  useEffect(() => {
    api.get<{ data: UserOpt[] }>('/portal/chat/directory').then(r => setUsers((r.data ?? []).filter(u => u.id !== me?.id))).catch(() => { /* noop */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id: string) => setSelected(s =>
    mode === 'dm' ? [id] : s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const create = async () => {
    if (selected.length === 0) { alert('相手を選んでください'); return; }
    if (mode === 'group' && !name.trim()) { alert('グループ名を入力してください'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ data: { id: string } }>('/portal/chat/rooms', { room_type: mode, name: name.trim(), member_ids: selected });
      onCreated(r.data.id);
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '作成に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Modal width={520} title="新しいチャット" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13.5, cursor: 'pointer' }}>
            <input type="radio" checked={mode === 'dm'} onChange={() => { setMode('dm'); setSelected(s => s.slice(0, 1)); }} /> 1対1
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13.5, cursor: 'pointer' }}>
            <input type="radio" checked={mode === 'group'} onChange={() => setMode('group')} /> グループ
          </label>
        </div>
        {mode === 'group' && (
          <div>
            <label style={labelStyle}>グループ名 *</label>
            <input style={fieldStyle} value={name} onChange={e => setName(e.target.value)} placeholder="例: 営業チーム" />
          </div>
        )}
        <div>
          <label style={labelStyle}>{mode === 'dm' ? '相手を選ぶ' : 'メンバーを選ぶ（複数可）'}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
            {users.map(u => (
              <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', background: selected.includes(u.id) ? '#e6f4f1' : '#fff' }}>
                <input type={mode === 'dm' ? 'radio' : 'checkbox'} checked={selected.includes(u.id)} onChange={() => toggle(u.id)} /> {u.user_name}
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={() => void create()}>{busy ? '作成中...' : '開始する'}</button>
        </div>
      </div>
    </Modal>
  );
}

function MembersModal({ roomId, members, onClose, onChanged, onLeft }: {
  roomId: string; members: Member[]; onClose: () => void; onChanged: () => void; onLeft: () => void;
}) {
  const [users, setUsers] = useState<UserOpt[]>([]);
  const memberIds = new Set(members.map(m => m.user_id));
  useEffect(() => {
    api.get<{ data: UserOpt[] }>('/portal/chat/directory').then(r => setUsers(r.data ?? [])).catch(() => { /* noop */ });
  }, []);
  return (
    <Modal width={480} title="メンバー" onClose={onClose}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {members.map(m => (
          <span key={m.user_id} style={{ fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 10px', background: '#f8fafc' }}>{m.user_name}</span>
        ))}
      </div>
      <div style={{ ...labelStyle }}>メンバーを追加</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 180, overflowY: 'auto', marginBottom: 14 }}>
        {users.filter(u => !memberIds.has(u.id)).map(u => (
          <button key={u.id} className="btn" style={{ fontSize: 12.5 }} onClick={async () => {
            try { await api.post(`/portal/chat/rooms/${roomId}/members`, { user_ids: [u.id] }); onChanged(); }
            catch (e: unknown) { alert(e instanceof Error ? e.message : '追加に失敗しました'); }
          }}>＋ {u.user_name}</button>
        ))}
      </div>
      <button className="btn" style={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: 5 }} onClick={async () => {
        if (!confirm('このグループから退出しますか？')) return;
        try { await api.post(`/portal/chat/rooms/${roomId}/leave`, {}); onLeft(); }
        catch (e: unknown) { alert(e instanceof Error ? e.message : '退出に失敗しました'); }
      }}>
        <LogOut size={13} /> グループから退出
      </button>
    </Modal>
  );
}
